import { create } from 'zustand';
import type { Socket } from 'socket.io-client';
import { emitAck, getSocket } from '../api/socket';
import type { Conversation, User } from '../api/types';
import { isPermissionDenied, showMediaHelp } from '../lib/mediaHelp';
import { startTone, stopTone } from '../lib/sound';

/**
 * 1:1 WebRTC calls. Signaling goes through Socket.IO (server/src/calls/signaling.ts);
 * the caller always creates the offer (also for ICE restarts), so there is no offer glare.
 *
 * Phases map to TЗ 3.5: outgoing = initiated/waiting for answer, incoming = ringing,
 * connecting = accepted/establishing, active = in call, ended = finished/rejected/failed.
 */
export type CallPhase = 'idle' | 'outgoing' | 'incoming' | 'connecting' | 'active' | 'ended';
type Media = 'audio' | 'video';

interface CallState {
  phase: CallPhase;
  direction: 'in' | 'out';
  callId: string | null;
  conversationId: string | null;
  media: Media;
  peer: User | null;
  muted: boolean;
  cameraOn: boolean;
  hasVideo: boolean;
  canSwitchCamera: boolean;
  /** Loudspeaker vs earpiece; only switchable where the browser exposes both routes (see findAudioRoutes). */
  speakerOn: boolean;
  canToggleSpeaker: boolean;
  remoteVideoOn: boolean;
  remoteMuted: boolean;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  startedAt: number | null;
  reconnecting: boolean;
  endReason: string | null;
  notice: string | null;
}

const idle: CallState = {
  phase: 'idle',
  direction: 'out',
  callId: null,
  conversationId: null,
  media: 'audio',
  peer: null,
  muted: false,
  cameraOn: false,
  hasVideo: false,
  canSwitchCamera: false,
  speakerOn: false,
  canToggleSpeaker: false,
  remoteVideoOn: false,
  remoteMuted: false,
  localStream: null,
  remoteStream: null,
  startedAt: null,
  reconnecting: false,
  endReason: null,
  notice: null,
};

export const useCall = create<CallState>(() => idle);
const get = useCall.getState;
const set = useCall.setState;

export const END_REASON_TEXT: Record<string, string> = {
  hangup: 'Звонок завершён',
  rejected: 'Звонок отклонён',
  no_answer: 'Нет ответа',
  busy: 'Абонент занят',
  answered_elsewhere: 'Принят на другом устройстве',
  connection_lost: 'Соединение потеряно',
  connection_failed: 'Не удалось установить соединение',
  media_denied: 'Нет доступа к микрофону',
  media_unavailable: 'Микрофон недоступен — возможно, его занимает другое приложение',
  media_unsupported: 'Браузер не поддерживает звонки (нужен HTTPS)',
  already_in_call: 'Вы уже участвуете в звонке',
  not_found: 'Звонок недоступен',
  offline: 'Нет соединения с сервером',
  server_shutdown: 'Сервер перезапускается',
  failed: 'Ошибка звонка',
};

let pc: RTCPeerConnection | null = null;
let iceServers: RTCIceServer[] = [];
let pendingCandidates: RTCIceCandidateInit[] = [];
let queuedSignals: any[] = [];
let failTimer: ReturnType<typeof setTimeout> | undefined;
let restartAttempted = false;
let cancelRequested = false;
let facing: 'user' | 'environment' = 'user';
let audioRouting: Promise<void> = Promise.resolve();
const endedEarly = new Map<string, string>();
let autoAnswer: { callId: string; until: number } | null = null;

const AUDIO: MediaTrackConstraints = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
const videoConstraints = (): MediaTrackConstraints => ({
  facingMode: facing,
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 30 },
});

function signal(data: Record<string, unknown>) {
  const { callId } = get();
  if (callId) getSocket()?.emit('call:signal', { callId, data });
}

function sendMediaState() {
  const s = get();
  signal({ type: 'media-state', video: s.hasVideo && s.cameraOn, muted: s.muted });
}

async function acquireMedia(media: Media): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) throw Object.assign(new Error('unsupported'), { reason: 'media_unsupported' });
  facing = 'user';
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: AUDIO, video: media === 'video' ? videoConstraints() : false });
  } catch (err) {
    if (media === 'video') {
      // Camera denied or missing: continue as an audio call.
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: AUDIO });
        set({ notice: 'Камера недоступна — звонок без видео' });
        return s;
      } catch {
        /* fall through */
      }
    }
    throw Object.assign(err as Error, { reason: isPermissionDenied(err) ? 'media_denied' : 'media_unavailable' });
  }
}

async function setLocalStream(stream: MediaStream) {
  const hasVideo = stream.getVideoTracks().length > 0;
  let canSwitchCamera = false;
  if (hasVideo) {
    const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
    canSwitchCamera = devices.filter((d) => d.kind === 'videoinput').length > 1;
  }
  // Like phone apps: voice calls start on the earpiece, video calls on the loudspeaker.
  set({ localStream: stream, hasVideo, cameraOn: hasVideo, canSwitchCamera, muted: false, speakerOn: hasVideo });
  navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
  await applyAudioRoute();
}

/**
 * Earpiece / loudspeaker. Web pages cannot pick the output directly, but Chrome on Android lists the phone's
 * communication routes as microphones with these fixed names, and opening the microphone by one of them
 * routes the call audio there too (Chromium AudioManagerAndroid). Left alone (the default microphone), Chrome
picks the loudspeaker, so only the earpiece is selected explicitly and video calls keep the untouched microphone.
 * With a headset connected the system's own choice (the headset) wins and there is nothing to toggle.
 * Other browsers (iPhone Safari, desktop) expose no such routes: the button is hidden and audio goes
 * wherever the system sends it.
 */
async function findAudioRoutes() {
  const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
  const inputs = devices.filter((d) => d.kind === 'audioinput');
  const id = (label: string) => inputs.find((d) => d.label === label)?.deviceId;
  const earpiece = id('Headset earpiece');
  const headset = inputs.some((d) => ['Wired headset', 'Bluetooth headset', 'USB audio'].includes(d.label));
  return earpiece && id('Speakerphone') && !headset ? { earpiece } : null;
}

/** Reopens the microphone on the route matching `speakerOn` (serialized: taps and device changes can overlap). */
function applyAudioRoute() {
  audioRouting = audioRouting.then(async () => {
    const routes = await findAudioRoutes();
    set({ canToggleSpeaker: Boolean(routes) });
    const s = get();
    const old = s.localStream?.getAudioTracks()[0];
    if (!old || !s.localStream || old.readyState === 'ended') return;
    // The default device is the system's choice: loudspeaker, or the headset once one is connected.
    const deviceId = routes && !s.speakerOn ? routes.earpiece : undefined;
    if (deviceId ? old.getSettings().deviceId === deviceId : !old.getConstraints().deviceId) return;
    const open = (audio: MediaTrackConstraints) =>
      navigator.mediaDevices.getUserMedia({ audio }).then((m) => m.getAudioTracks()[0], () => null);
    const wanted = deviceId ? { ...AUDIO, deviceId: { exact: deviceId } } : AUDIO;
    let track = await open(wanted);
    if (!track) {
      // Some phones cannot open a second microphone: release the current one first.
      old.stop();
      track = (await open(wanted)) ?? (await open(AUDIO));
      if (!track) return set({ notice: 'Не удалось переключить звук' });
    }
    const now = get();
    if (!now.localStream || now.localStream.getAudioTracks()[0] !== old) {
      track.stop(); // the call ended meanwhile
      return;
    }
    track.enabled = !now.muted;
    await pc?.getSenders().find((x) => x.track === old || x.track?.kind === 'audio')?.replaceTrack(track).catch(() => {});
    old.stop();
    set({ localStream: new MediaStream([track, ...now.localStream.getVideoTracks()]) });
  });
  return audioRouting;
}

function onDeviceChange() {
  if (get().localStream) void applyAudioRoute();
}

function createPeer() {
  pc?.close();
  restartAttempted = false;
  pendingCandidates = [];
  const peer = new RTCPeerConnection({ iceServers });
  pc = peer;
  const local = get().localStream;
  local?.getTracks().forEach((t) => peer.addTrack(t, local));

  peer.onicecandidate = (e) => e.candidate && signal({ type: 'candidate', candidate: e.candidate.toJSON() });
  peer.ontrack = (e) => {
    const stream = e.streams[0] ?? new MediaStream([e.track]);
    set({ remoteStream: stream, remoteVideoOn: stream.getVideoTracks().length > 0 || get().remoteVideoOn });
  };
  const onState = () => {
    if (pc !== peer) return;
    const st = peer.connectionState ?? peer.iceConnectionState;
    if (st === 'connected' || peer.iceConnectionState === 'connected' || peer.iceConnectionState === 'completed') {
      clearTimeout(failTimer);
      failTimer = undefined;
      if (get().phase !== 'active') void reportDiag(peer, 'connected');
      set({ phase: 'active', reconnecting: false, startedAt: get().startedAt ?? Date.now() });
    } else if (st === 'disconnected') {
      set({ reconnecting: true });
      scheduleFailure(8000);
    } else if (st === 'failed') {
      set({ reconnecting: true });
      void restartIce();
    }
  };
  peer.onconnectionstatechange = onState;
  peer.oniceconnectionstatechange = onState;

  // Signals that arrived before the peer existed (callee: offer can race the accept ack).
  const queued = queuedSignals;
  queuedSignals = [];
  queued.forEach((d) => void handleSignal(d));
}

function scheduleFailure(ms: number) {
  if (failTimer) return;
  failTimer = setTimeout(() => {
    failTimer = undefined;
    const st = pc?.connectionState ?? pc?.iceConnectionState;
    if (st === 'connected') return;
    if (!restartAttempted) void restartIce();
    else fail('connection_failed');
  }, ms);
}

async function restartIce() {
  if (!pc) return;
  if (restartAttempted) return scheduleFailure(15000);
  restartAttempted = true;
  if (get().direction === 'out') {
    const offer = await pc.createOffer({ iceRestart: true });
    await pc.setLocalDescription(offer);
    signal({ type: 'offer', sdp: offer.sdp });
  } else {
    signal({ type: 'restart' });
  }
  scheduleFailure(15000);
}

async function flushCandidates() {
  const list = pendingCandidates;
  pendingCandidates = [];
  for (const c of list) await pc?.addIceCandidate(c).catch(() => {});
}

async function handleSignal(data: any) {
  if (!pc) {
    queuedSignals.push(data);
    return;
  }
  try {
    switch (data.type) {
      case 'offer': {
        await pc.setRemoteDescription({ type: 'offer', sdp: data.sdp });
        await flushCandidates();
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        signal({ type: 'answer', sdp: answer.sdp });
        sendMediaState();
        break;
      }
      case 'answer':
        await pc.setRemoteDescription({ type: 'answer', sdp: data.sdp });
        await flushCandidates();
        sendMediaState();
        break;
      case 'candidate':
        if (pc.remoteDescription) await pc.addIceCandidate(data.candidate).catch(() => {});
        else pendingCandidates.push(data.candidate);
        break;
      case 'media-state':
        set({ remoteVideoOn: Boolean(data.video), remoteMuted: Boolean(data.muted) });
        break;
      case 'restart':
        if (get().direction === 'out') {
          restartAttempted = false;
          await restartIce();
        }
        break;
    }
  } catch (err) {
    console.warn('signal handling failed', err);
  }
}

/**
 * Connection diagnostics for the server log: which ICE route was used (host / srflx = via STUN / relay =
 * via TURN) or, when it failed, which candidate types this device managed to gather. No IP addresses.
 */
async function reportDiag(peer: RTCPeerConnection, event: string) {
  const callId = get().callId;
  if (!callId) return;
  // Read before awaiting: on hangup the tracks are stopped and the player detached right after this call.
  const mic = get().localStream?.getAudioTracks()[0];
  const player = document.querySelector<HTMLAudioElement>('.call-overlay audio');
  const audio: Record<string, unknown> = {
    route: mic ? mic.label.slice(0, 40) : null,
    track: mic ? `${mic.readyState}${mic.enabled ? '' : ',off'}${mic.muted ? ',muted' : ''}` : null,
    playing: player ? !player.paused && Boolean(player.srcObject) : null,
  };
  try {
    const stats = await peer.getStats();
    const byId = new Map<string, any>();
    stats.forEach((r) => byId.set(r.id, r));
    const gathered: Record<string, number> = {};
    let pair: any;
    stats.forEach((r: any) => {
      if (r.kind === 'audio' && r.type === 'media-source') audio.micEnergy = r.totalAudioEnergy;
      if (r.kind === 'audio' && r.type === 'outbound-rtp') audio.sent = r.packetsSent;
      if (r.kind === 'audio' && r.type === 'inbound-rtp') Object.assign(audio, { received: r.packetsReceived, inEnergy: r.totalAudioEnergy });
      if (r.type === 'local-candidate') gathered[r.candidateType] = (gathered[r.candidateType] ?? 0) + 1;
      if (r.type === 'transport' && r.selectedCandidatePairId) pair = byId.get(r.selectedCandidatePairId);
    });
    if (!pair) stats.forEach((r: any) => r.type === 'candidate-pair' && r.state === 'succeeded' && r.nominated && (pair = r));
    getSocket()?.emit('call:diag', {
      callId,
      event,
      ice: peer.iceConnectionState,
      local: pair ? byId.get(pair.localCandidateId)?.candidateType : null,
      remote: pair ? byId.get(pair.remoteCandidateId)?.candidateType : null,
      protocol: pair ? byId.get(pair.localCandidateId)?.protocol : null,
      gathered,
      turn: iceServers.some((s) => [s.urls].flat().some((u) => String(u).startsWith('turn'))),
      audio,
    });
  } catch {
    /* diagnostics are best effort */
  }
}

function cleanup() {
  stopTone();
  clearTimeout(failTimer);
  failTimer = undefined;
  const peer = pc;
  pc = null;
  if (peer) {
    // Report how the call ended network-wise before closing the connection.
    const reported = get().phase === 'active' ? 'ended' : 'ended-before-connected';
    void reportDiag(peer, reported).finally(() => peer.close());
  }
  queuedSignals = [];
  pendingCandidates = [];
  get().localStream?.getTracks().forEach((t) => t.stop());
  navigator.mediaDevices?.removeEventListener('devicechange', onDeviceChange);
}

function finish(reason: string) {
  const callId = get().callId;
  cleanup();
  set({ phase: 'ended', endReason: reason, localStream: null, remoteStream: null, reconnecting: false });
  setTimeout(() => {
    if (get().phase === 'ended' && get().callId === callId) set(idle);
  }, 2500);
}

function fail(reason: string) {
  const { callId } = get();
  if (callId) getSocket()?.emit('call:fail', { callId, reason });
  finish(reason);
}

const busy = () => get().phase !== 'idle' && get().phase !== 'ended';

// ── Public API ────────────────────────────────────────────────────────────────

export async function startCall(conversation: Conversation, media: Media) {
  if (busy()) return;
  cancelRequested = false;
  set({ ...idle, phase: 'outgoing', direction: 'out', media, peer: conversation.peer, conversationId: conversation.id });
  let stream: MediaStream;
  try {
    stream = await acquireMedia(media);
  } catch (err: any) {
    if (err.reason === 'media_denied') showMediaHelp(media === 'video');
    return finish(err.reason ?? 'media_denied');
  }
  if (cancelRequested) {
    stream.getTracks().forEach((t) => t.stop());
    return finish('hangup');
  }
  await setLocalStream(stream);
  let res: { callId?: string; iceServers?: RTCIceServer[]; error?: string };
  try {
    res = await emitAck('call:start', { conversationId: conversation.id, media });
  } catch {
    return finish('offline');
  }
  if (!res.callId) return finish(res.error ?? 'failed');
  iceServers = res.iceServers ?? [];
  set({ callId: res.callId });
  const early = endedEarly.get(res.callId);
  if (early) return finish(early);
  if (cancelRequested) return hangup();
  startTone('outgoing');
}

export async function acceptCall() {
  const { callId, media, phase } = get();
  if (phase !== 'incoming' || !callId) return;
  stopTone();
  set({ phase: 'connecting' });
  let stream: MediaStream;
  try {
    stream = await acquireMedia(media);
  } catch (err: any) {
    getSocket()?.emit('call:reject', { callId });
    if (err.reason === 'media_denied') showMediaHelp(media === 'video');
    return finish(err.reason ?? 'media_denied');
  }
  if (get().callId !== callId || get().phase !== 'connecting') {
    stream.getTracks().forEach((t) => t.stop());
    return;
  }
  await setLocalStream(stream);
  let res: { ok?: boolean; iceServers?: RTCIceServer[]; error?: string };
  try {
    res = await emitAck('call:accept', { callId });
  } catch {
    return finish('offline');
  }
  if (!res.ok) return finish(res.error === 'already_answered' ? 'answered_elsewhere' : (res.error ?? 'failed'));
  iceServers = res.iceServers ?? [];
  createPeer();
  scheduleFailure(30000);
}

export function rejectCall() {
  const { callId } = get();
  if (callId) getSocket()?.emit('call:reject', { callId });
  finish('rejected');
}

export function hangup() {
  const { callId, phase } = get();
  if (phase === 'outgoing' && !callId) {
    cancelRequested = true;
    return;
  }
  if (callId) getSocket()?.emit('call:hangup', { callId });
  finish('hangup');
}

export function toggleMute() {
  const muted = !get().muted;
  get().localStream?.getAudioTracks().forEach((t) => (t.enabled = !muted));
  set({ muted });
  sendMediaState();
}

export function toggleSpeaker() {
  if (!get().canToggleSpeaker) return;
  set({ speakerOn: !get().speakerOn });
  void applyAudioRoute();
}

/** Turning the camera off keeps the call going as audio (track disabled, no renegotiation). */
export function toggleCamera() {
  const s = get();
  if (!s.hasVideo) return;
  const cameraOn = !s.cameraOn;
  s.localStream?.getVideoTracks().forEach((t) => (t.enabled = cameraOn));
  set({ cameraOn });
  sendMediaState();
}

export async function switchCamera() {
  const s = get();
  const old = s.localStream?.getVideoTracks()[0];
  if (!old || !s.localStream) return;
  facing = facing === 'user' ? 'environment' : 'user';
  try {
    old.stop(); // some mobile browsers cannot open two cameras at once
    const fresh = await navigator.mediaDevices.getUserMedia({ video: videoConstraints() });
    const track = fresh.getVideoTracks()[0];
    track.enabled = s.cameraOn;
    await pc?.getSenders().find((x) => x.track?.kind === 'video' || x.track === old)?.replaceTrack(track);
    const stream = new MediaStream([...(get().localStream ?? s.localStream).getAudioTracks(), track]);
    set({ localStream: stream });
  } catch {
    facing = facing === 'user' ? 'environment' : 'user';
    set({ notice: 'Не удалось переключить камеру' });
  }
}

/** "Answer" on the ringing notification: accept this call as soon as its invite is on screen. */
export function answerWhenRinging(callId: string) {
  const s = get();
  if (s.phase === 'incoming' && s.callId === callId) return void acceptCall();
  autoAnswer = { callId, until: Date.now() + 60_000 };
}

/** Handles `?call=<id>&answer=1` from a notification; removes the parameters from the address bar. */
export function handleCallLink(href: string): string {
  const url = new URL(href, location.origin);
  const callId = url.searchParams.get('call');
  if (callId && url.searchParams.get('answer') === '1') answerWhenRinging(callId);
  url.searchParams.delete('call');
  url.searchParams.delete('answer');
  return url.pathname + url.search + url.hash;
}

/** Binds call events to a (new) socket. Called on every socket creation. */
export function bindCallSocket(socket: Socket) {
  socket.on('call:incoming', (inv: { callId: string; conversationId: string; media: Media; from: User; expiresAt: number }) => {
    if (busy()) return;
    if (get().phase === 'ended') set(idle);
    set({ ...idle, phase: 'incoming', direction: 'in', callId: inv.callId, conversationId: inv.conversationId, media: inv.media, peer: inv.from });
    if (autoAnswer?.callId === inv.callId && Date.now() < autoAnswer.until) {
      autoAnswer = null;
      void acceptCall();
      return;
    }
    startTone('incoming');
  });

  socket.on('call:accepted', async ({ callId }: { callId: string }) => {
    if (get().callId !== callId || get().phase !== 'outgoing') return;
    stopTone();
    set({ phase: 'connecting' });
    createPeer();
    try {
      const offer = await pc!.createOffer();
      await pc!.setLocalDescription(offer);
      signal({ type: 'offer', sdp: offer.sdp });
      scheduleFailure(30000);
    } catch {
      fail('connection_failed');
    }
  });

  socket.on('call:signal', ({ callId, data }: { callId: string; data: any }) => {
    if (get().callId === callId) void handleSignal(data);
  });

  socket.on('call:ended', ({ callId, reason }: { callId: string; reason: string }) => {
    if (get().callId === callId) {
      if (get().phase !== 'ended') finish(reason);
    } else if (get().phase === 'outgoing' && !get().callId) {
      endedEarly.set(callId, reason);
      setTimeout(() => endedEarly.delete(callId), 10000);
    }
  });

  socket.on('call:peer-reconnecting', ({ callId }: { callId: string }) => {
    if (get().callId === callId) set({ reconnecting: true });
  });
  socket.on('call:peer-reconnected', ({ callId }: { callId: string }) => {
    if (get().callId === callId && pc?.connectionState === 'connected') set({ reconnecting: false });
  });

  // Signaling reconnected mid-call: re-bind this device to the call on the server.
  socket.on('connect', async () => {
    const { callId, phase } = get();
    if (!callId || !['outgoing', 'connecting', 'active'].includes(phase)) return;
    try {
      const res = await emitAck<{ ok?: boolean }>('call:rejoin', { callId });
      if (!res.ok) finish('connection_lost');
    } catch {
      /* next reconnect will retry */
    }
  });
}
